import React, { useEffect, useRef, useState } from 'react';
import { EDAutoCompleteEdit } from '../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { useIsExtraSmallScreen } from '../../Hooks/useFullscreenView';
import { IDatabase } from '../../Features/Login/Contexts/LoginStateContext/LoginStateContext';
import EDIcon from '../../BaseComponents/Icon/EDIcon';
import EDTooltip from '../../BaseComponents/Tooltip/EDTooltip';
import EDTypography from '../../BaseComponents/Typography/EDTypography';
import { makeStyles } from 'tss-react/mui';

interface IProps {
  selectedDatabase?: string;
  databases: IDatabase[];
  onDatabaseChange: (db: string) => void;
  fixedWidth?: boolean;
}

const useStyles = makeStyles()(theme => ({
  databaseNameText: {
    display: 'inline',
    color: 'inherit',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  selectContainer: {
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.customProperties.AutocompleteColors.background,
    },
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.8, 1, 0.8, 1.5),
    borderRadius: '4px',
  },
}));

const DatabaseAutocompleteSelect: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const isXs = useIsExtraSmallScreen();
  const [focused, setFocused] = useState(false);
  const { classes } = useStyles();
  const autoCompleteRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (focused && autoCompleteRef.current) {
      // Use setTimeout to ensure the DOM has updated before focusing
      setTimeout(() => {
        const autoCompleteElement = autoCompleteRef.current;
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        });
        autoCompleteElement?.dispatchEvent(clickEvent);
      }, 0);
    }
  }, [focused]);

  if (focused) {
    return (
      <>
        <EDAutoCompleteEdit
          hideOutline
          onDarkBackground
          options={props.databases.map(db => db.name)}
          getOptionLabel={(option: string) => option}
          value={props.selectedDatabase}
          fullWidth={isXs}
          width={props.fixedWidth ? '193px' : undefined}
          hideClear
          onChange={(option: string | null) => {
            if (option && !Array.isArray(option)) {
              props.onDatabaseChange(option);
              setFocused(false);
            }
          }}
          onBlur={() => {
            setFocused(false);
          }}
          ref={autoCompleteRef}
        />
      </>
    );
  } else {
    return (
      <EDTooltip title={isXs ? props.selectedDatabase : null}>
        <span
          onClick={() => {
            setFocused(true);
          }}
          className={classes.selectContainer}
        >
          <EDTypography className={classes.databaseNameText}>
            {props.selectedDatabase}
          </EDTypography>
          <EDIcon iconType={'arrow_drop_down'} color="inherit" />
        </span>
      </EDTooltip>
    );
  }
};

export default DatabaseAutocompleteSelect;
